import { ActiveLeadState, LeadStatus } from '@bariendo/lib/active-lead';
import { useCallback, useMemo, useState } from 'react';
import { StickyNoteCell } from '../../services/listRowShared';
import { QUEUE_FILTER_ANY } from '../../services/queueFilters';
import { useLeadStatusChanges } from './useLeadStatusChanges';

/**
 * What a hub tab's rows pick up from edits made in the table: notes saved from
 * the Notes column and lead statuses changed from the Status column, shown
 * straight away, plus the faded look of a row waiting on Undo (BAR-2878).
 */

export interface HubEditableRow {
  patient?: { id?: string };
  stickyNote?: StickyNoteCell;
  /** Absent on a tab with no Status column, such as Won. */
  activeLead?: ActiveLeadState;
}

export type EditedRow<Row> = Row & { statusUndoSecondsLeft?: number };

export function useHubRowEdits<Row extends HubEditableRow>(rows: Row[], statusFilter: string) {
  const [savedNotes, setSavedNotes] = useState<Map<string, StickyNoteCell>>(new Map());
  const onNoteSaved = useCallback(
    (patientId: string, note: StickyNoteCell) => setSavedNotes((prev) => new Map(prev).set(patientId, note)),
    []
  );

  const matchesFilter = useCallback(
    (status: LeadStatus) => statusFilter === QUEUE_FILTER_ANY || status === statusFilter,
    [statusFilter]
  );
  const { saved, pending, leaving, change, undo } = useLeadStatusChanges({ matchesFilter });

  const editedRows = useMemo(
    () =>
      rows.map((row): EditedRow<Row> => {
        const patientId = row.patient?.id ?? '';
        const note = savedNotes.get(patientId);
        const status = saved.get(patientId);
        return {
          ...row,
          ...(note ? { stickyNote: note } : {}),
          ...(status && row.activeLead ? { activeLead: { ...row.activeLead, status } } : {}),
          statusUndoSecondsLeft: pending.get(patientId)?.secondsLeft,
        };
      }),
    [rows, savedNotes, saved, pending]
  );

  const rowClassName = (row: EditedRow<Row>): string | undefined => {
    // Loading placeholders reach here with no patient.
    const patientId = row.patient?.id;
    if (patientId && leaving.has(patientId)) {
      return 'queue-row-leaving';
    }
    return row.statusUndoSecondsLeft !== undefined ? 'queue-row-parking' : undefined;
  };

  return { rows: editedRows, onNoteSaved, changeStatus: change, undoStatus: undo, rowClassName };
}
